import Button from "@/components/Button";
import TextField from "@/components/TextField";
import { useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { PATCH_FACILITY_MUTATION } from "@/features/facility/queries";
import { EditProps } from "./types";

const NameSchema = z.object({
  name: z.string().min(1, "Name is required")
});

type FieldValues = z.infer<typeof NameSchema>;

interface NameEditFormProps extends EditProps {
  name: string;
}

const defaultErrorMessage = "Failed to update facility name";

export default function NameEditForm({
  facilityId,
  name: initialName,
  onCancel,
  onSuccess,
  onError
}: NameEditFormProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      name: initialName
    },
    resolver: zodResolver(NameSchema),
    mode: "onChange"
  });

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);

  async function onSubmit({ name }: FieldValues) {
    try {
      const input = {
        id: facilityId,
        name
      };

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="name-edit-form">
      <TextField
        className="w-full"
        label="Facility Name"
        value={form.watch("name")}
        {...form.register("name")}
        disabled={loading}
        error={!!form.formState.errors.name}
        errorText={form.formState.errors.name?.message}
      />
      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
